import React, { useId } from 'react';
import type { ToggleProps, ToggleSize } from './types';

// ─── Size class map (exported for snapshot testing) ──────────────────────────

export const SIZE_CLASSES: Record<
  ToggleSize,
  { track: string; thumb: string; thumbOn: string; thumbOff: string }
> = {
  // track: 32×18px  thumb: 14px
  sm: {
    track: 'w-8 h-[18px]',
    thumb: 'h-3.5 w-3.5 top-[2px]',
    thumbOff: 'left-[2px]',
    thumbOn: 'left-[calc(100%-18px)]',
  },
  // track: 44×24px  thumb: 18px
  md: {
    track: 'w-11 h-6',
    thumb: 'h-[18px] w-[18px] top-[3px]',
    thumbOff: 'left-[3px]',
    thumbOn: 'left-[calc(100%-21px)]',
  },
};

// ─── Toggle ───────────────────────────────────────────────────────────────────

export function Toggle({
  checked = false,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  id: idProp,
  className,
}: ToggleProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const descId = description ? `${id}-desc` : undefined;

  const s = SIZE_CLASSES[size];

  const handleClick = () => {
    if (!disabled) onChange?.(!checked);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!disabled) onChange?.(!checked);
    }
  };

  const trackClasses = [
    'relative inline-flex shrink-0 rounded-full transition-colors duration-200',
    s.track,
    checked
      ? 'bg-brand-500'
      : 'bg-neutral-300 dark:bg-neutral-600',
    disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'cursor-pointer hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
  ]
    .filter(Boolean)
    .join(' ');

  const thumbClasses = [
    'absolute rounded-full bg-white shadow-xs transition-all duration-200',
    s.thumb,
    checked ? s.thumbOn : s.thumbOff,
  ].join(' ');

  const track = (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-describedby={descId}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={trackClasses}
    >
      <span className={thumbClasses} />
      <span className="sr-only">{label ?? (checked ? 'On' : 'Off')}</span>
    </button>
  );

  if (!label && !description) {
    return <div className={className}>{track}</div>;
  }

  return (
    <div className={['flex items-start gap-3', className ?? ''].filter(Boolean).join(' ')}>
      {track}
      <div className="flex flex-col">
        {label && (
          <label
            htmlFor={id}
            className={[
              'text-sm font-medium leading-6',
              disabled
                ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
                : 'text-neutral-700 dark:text-neutral-300 cursor-pointer',
            ].join(' ')}
          >
            {label}
          </label>
        )}
        {description && (
          <span
            id={descId}
            className="text-xs text-neutral-500 dark:text-neutral-400"
          >
            {description}
          </span>
        )}
      </div>
    </div>
  );
}
